import React, { useEffect, useState } from 'react';
import { Dialog, T, useTranslation } from '@colonist/ui';
import { emptyTranslation } from '@colonist/i18n';
import { ArrayUtils, delay } from '@colonist/utils';
import { DICE_FACES, INITIAL_DICE_FACE } from './constants.ts';
import styles from './FreePlayDiceRollDialog.module.scss';
const IDLE_CYCLE_INTERVAL_MS = 450;
const FAST_CYCLE_INTERVAL_MS = 70;
const SETTLE_SLOW_INTERVAL_MS = 360;
const SETTLE_FAST_DURATION_MS = 1000;
const SETTLE_DECELERATE_DURATION_MS = 1800;
const LAND_DELAY_MS = 300;
const LAND_DURATION_MS = 280;
function getEligibleRollsText(eligibleRolls, language) {
    const rollStrings = eligibleRolls.map((roll)=>roll.toString());
    return new Intl.ListFormat(language, {
        type: 'disjunction'
    }).format(rollStrings);
}
function getRandomFaceExcluding(currentFace) {
    const otherFaces = DICE_FACES.filter((face)=>face != currentFace);
    return ArrayUtils.randomElementFromArray(otherFaces);
}
function getDeceleratingIntervalMs(progress) {
    const eased = progress * progress;
    return FAST_CYCLE_INTERVAL_MS + (SETTLE_SLOW_INTERVAL_MS - FAST_CYCLE_INTERVAL_MS) * eased;
}
/** Dialog letting an eligible player roll a die for a free ranked Cities & Knights game. */ export function FreePlayDiceRollDialog({ open, onClose, eligibleRolls, onRoll, onPlayRanked }) {
    const { language } = useTranslation();
    const [phase, setPhase] = useState('idle');
    const [diceFace, setDiceFace] = useState(INITIAL_DICE_FACE);
    const [animationClassName, setAnimationClassName] = useState('');
    const [diceRoll, setDiceRoll] = useState(0);
    useEffect(()=>{
        if (!open) return;
        setPhase('idle');
        setDiceFace(INITIAL_DICE_FACE);
        setAnimationClassName('');
    }, [
        open
    ]);
    useEffect(()=>{
        const isCyclingFaces = phase == 'idle' || phase == 'rolling';
        if (!isCyclingFaces) return;
        const cycleIntervalMs = phase == 'idle' ? IDLE_CYCLE_INTERVAL_MS : FAST_CYCLE_INTERVAL_MS;
        const intervalId = setInterval(()=>setDiceFace(getRandomFaceExcluding), cycleIntervalMs);
        return ()=>clearInterval(intervalId);
    }, [
        phase
    ]);
    const runSettleAnimation = async (finalFace)=>{
        for(let elapsedMs = 0; elapsedMs < SETTLE_FAST_DURATION_MS; elapsedMs += FAST_CYCLE_INTERVAL_MS){
            setDiceFace(getRandomFaceExcluding);
            await delay(FAST_CYCLE_INTERVAL_MS);
        }
        for(let elapsedMs = 0; elapsedMs < SETTLE_DECELERATE_DURATION_MS;){
            const intervalMs = getDeceleratingIntervalMs(elapsedMs / SETTLE_DECELERATE_DURATION_MS);
            setDiceFace(getRandomFaceExcluding);
            await delay(intervalMs);
            elapsedMs += intervalMs;
        }
        setDiceFace(finalFace);
    };
    const handleRoll = async ()=>{
        setPhase('rolling');
        const result = await onRoll();
        const rolledFace = DICE_FACES.find((face)=>face.value == (result === null || result === void 0 ? void 0 : result.roll));
        if (result == undefined || rolledFace == undefined) {
            setPhase('idle');
            return;
        }
        setPhase('settling');
        await runSettleAnimation(rolledFace);
        await delay(LAND_DELAY_MS);
        setAnimationClassName(styles.diceLand);
        await delay(LAND_DURATION_MS);
        setAnimationClassName('');
        setDiceRoll(result.roll);
        if (result.isWinningRoll) setPhase('success');
        else setPhase('failure');
    };
    return /*#__PURE__*/ React.createElement(Dialog, {
        open: open,
        onClose: onClose,
        size: "md"
    }, /*#__PURE__*/ React.createElement(Dialog.Title, null, /*#__PURE__*/ React.createElement(Dialog.ImageTitle, {
        image: {
            src: diceFace.image,
            alt: emptyTranslation
        },
        imageClassName: animationClassName
    }, /*#__PURE__*/ React.createElement(T, {
        id: {
            key: 'strings:popups.freePlayDiceRoll.title'
        }
    }))), /*#__PURE__*/ React.createElement(Dialog.Content, null, /*#__PURE__*/ React.createElement("div", {
        className: styles.content
    }, /*#__PURE__*/ React.createElement("p", null, /*#__PURE__*/ React.createElement(T, {
        id: {
            key: 'strings:popups.freePlayDiceRoll.body',
            options: {
                eligibleRolls: getEligibleRollsText(eligibleRolls, language)
            }
        }
    })), phase == 'success' && /*#__PURE__*/ React.createElement("p", null, /*#__PURE__*/ React.createElement(T, {
        id: {
            key: 'strings:popups.freePlayDiceRoll.success',
            options: {
                roll: diceRoll
            }
        }
    })), phase == 'failure' && /*#__PURE__*/ React.createElement("p", null, /*#__PURE__*/ React.createElement(T, {
        id: {
            key: 'strings:popups.freePlayDiceRoll.failure',
            options: {
                roll: diceRoll
            }
        }
    })))), /*#__PURE__*/ React.createElement(Dialog.ButtonFooter, null, /*#__PURE__*/ React.createElement(Dialog.ButtonFooter.CancelButton, {
        onClick: onClose
    }), phase == 'success' && /*#__PURE__*/ React.createElement(Dialog.ButtonFooter.ConfirmButton, {
        onClick: onPlayRanked
    }, /*#__PURE__*/ React.createElement(T, {
        id: {
            key: 'strings:popups.freePlayDiceRoll.playNow'
        }
    })), phase != 'success' && phase != 'failure' && /*#__PURE__*/ React.createElement(Dialog.ButtonFooter.ConfirmButton, {
        onClick: handleRoll
    }, /*#__PURE__*/ React.createElement(T, {
        id: {
            key: 'strings:popups.freePlayDiceRoll.checkButton'
        }
    }))));
}
